import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ClipboardCheck, Loader2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface StartReviewButtonProps {
  jobId: string;
  isClaimed: boolean;
  className?: string;
  onClaimed?: () => void;
}

/**
 * FR-077: "Start Review" CTA — visible to reviewer/admin for ALL decisions.
 * No auto-approve path exists. All gate outcomes route to human review.
 * Calls PATCH /api/v1/jobs/{id}/claim (soft claim) then navigates to review.
 */
export function StartReviewButton({
  jobId,
  isClaimed,
  className,
  onClaimed,
}: StartReviewButtonProps) {
  const { canReview } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only reviewers and admins may claim and review
  if (!canReview) return null;

  async function handleClick() {
    setIsLoading(true);
    setError(null);
    try {
      await api.patch(`/api/v1/jobs/${jobId}/claim`);
      onClaimed?.();
      navigate(`/jobs/${jobId}/review`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Already claimed — navigate anyway for review viewing
        navigate(`/jobs/${jobId}/review`);
      } else {
        setError('Failed to claim job. Please try again.');
        setIsLoading(false);
      }
    }
  }

  const label = isClaimed ? 'Continue Review' : 'Claim and Review';

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Button
        onClick={() => { void handleClick(); }}
        disabled={isLoading}
        size="lg"
        className="gap-2"
        aria-label={label}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <ClipboardCheck className="h-4 w-4" aria-hidden="true" />
        )}
        {isLoading ? 'Claiming...' : label}
      </Button>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
